import React from 'react';
import styled from 'styled-components';
import { Send } from 'lucide-react';
import { MicIcon, SoundWaveIcon } from '../../../../components/Icons';

interface ComposeViewProps {
  officeName: string;
  messageText: string;
  onMessageTextChange: (text: string) => void;
  hideFromPatient: boolean;
  onHideFromPatientChange: (checked: boolean) => void;
  onVoiceInput: () => void;
  onSend: () => void;
  isSending: boolean;
  isRecording: boolean;
  isProcessing?: boolean;
  showHideFromPatient?: boolean;
  onInputFocusChange?: (focused: boolean) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${({ theme }) => theme.spacing.ms};
  overflow-y: auto;
`;

const DestinationLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 80px;
  padding: ${({ theme }) => theme.spacing.ms};
  border: 1px solid ${({ theme }) => theme.colors.border.input};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-family: inherit;
  line-height: 1.6;
  resize: none;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const ActionArea = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.pageBackground};
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ButtonRow = styled.div`
  display: flex;
  margin-top: ${({ theme }) => theme.spacing.xs};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
`;

const VoiceButton = styled.button<{ $isRecording?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.ms};
  min-height: 44px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ $isRecording, theme }) => $isRecording ? theme.colors.backgroundTableGood : 'white'};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  & > svg {
    flex-shrink: 0;
  }
`;

const SendButton = styled.button<{ $hidden?: boolean }>`
  flex: ${({ $hidden }) => ($hidden ? 0 : 2)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-left: ${({ $hidden, theme }) => ($hidden ? 0 : theme.spacing.ms)};
  padding: ${({ $hidden, theme }) => ($hidden ? 0 : theme.spacing.ms)};
  min-width: ${({ $hidden }) => ($hidden ? 0 : 'auto')};
  max-width: ${({ $hidden }) => ($hidden ? '0px' : '100%')};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  overflow: hidden;
  white-space: nowrap;
  min-height: ${({ $hidden }) => ($hidden ? 0 : '44px')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ComposeView: React.FC<ComposeViewProps> = ({
  officeName,
  messageText,
  onMessageTextChange,
  hideFromPatient,
  onHideFromPatientChange,
  onVoiceInput,
  onSend,
  isSending,
  isRecording,
  isProcessing = false,
  showHideFromPatient = true,
  onInputFocusChange,
}) => {
  const handleFocus = () => {
    onInputFocusChange?.(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      onInputFocusChange?.(false);
    }, 150);
  };

  return (
    <Container>
      <DestinationLabel>{officeName}へ</DestinationLabel>
      <TextArea
        value={messageText}
        onChange={(e) => onMessageTextChange(e.target.value)}
        placeholder="メッセージを書く"
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      <ActionArea>
        {showHideFromPatient && (
          <CheckboxRow>
            <Checkbox
              type="checkbox"
              checked={hideFromPatient}
              onChange={(e) => onHideFromPatientChange(e.target.checked)}
            />
            <CheckboxLabel>非表示設定(利用者本人に共有しない)</CheckboxLabel>
          </CheckboxRow>
        )}
        <ButtonRow>
          <VoiceButton
            type="button"
            onClick={onVoiceInput}
            $isRecording={isRecording || isProcessing}
            disabled={isProcessing}
          >
            {isRecording ? <SoundWaveIcon /> : <MicIcon />}
            {isRecording ? '聞き取り中...' : isProcessing ? '変換中...' : '音声入力'}
          </VoiceButton>
          <SendButton
            type="button"
            $hidden={isRecording || isProcessing}
            onClick={onSend}
            disabled={!messageText.trim() || isSending}
          >
            <Send size={20} />
            送信する
          </SendButton>
        </ButtonRow>
      </ActionArea>
    </Container>
  );
};

export default ComposeView;
